import { Controller } from "@hotwired/stimulus"
import { formatCurrency, parseNumber } from "../modules/salary_lab/formatter"

export default class extends Controller {
  static targets = [
    "container", "payRiseButtons", "currentSalaryLabel",
    "riseResult5k", "riseResult10k", "riseResult20k",
    "kiwisaverImpact", "studentLoanImpact", "contractorLink",
    "ksRateButtons", "ksRateBtn", "ksResult",
    "careerProgression", "careerProgressionText"
  ]

  static values = {
    salary: Number,
    netSalary: Number,
    kiwisaverPerc: Number,
    hasStudentLoan: Boolean
  }

  connect() {
    this._lastResult = null
  }

  updateFromCalculation(event) {
    const result = event.detail
    if (!result || !result.gross_salary) return

    this._lastResult = result
    this.salaryValue = result.gross_salary
    this.netSalaryValue = result.net_salary
    this.kiwisaverPercValue = this._deriveKsRate(result)
    this.hasStudentLoanValue = (result.student_loan || 0) > 0

    this._renderScenarios(result)
    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove("hidden")
    }
  }

  async applyRise(event) {
    event.preventDefault()
    const amount = parseInt(event.currentTarget.dataset.amount, 10)
    if (!amount || !this.salaryValue) return

    const newSalary = this.salaryValue + amount

    try {
      const { calculate } = await import("../modules/salary_lab/calculator_api")
      const result = await calculate({
        gross_salary: newSalary,
        kiwisaver_perc: this.kiwisaverPercValue,
        include_student_loan: this.hasStudentLoanValue
      })
      this._showRiseResult(amount, result)
    } catch {
      // Silently handle
    }
  }

  async applyKsChange(event) {
    event.preventDefault()
    const rate = parseFloat(event.currentTarget.dataset.rate)
    if (!rate || !this.salaryValue) return

    try {
      const { calculate } = await import("../modules/salary_lab/calculator_api")
      const result = await calculate({
        gross_salary: this.salaryValue,
        kiwisaver_perc: rate,
        include_student_loan: this.hasStudentLoanValue
      })

      const diff = result.net_salary - this.netSalaryValue
      const fortnightlyDiff = diff / 26
      const target = this._ksResultTargetForRate(rate)
      if (target) {
        const sign = fortnightlyDiff >= 0 ? "+" : ""
        target.textContent = `${sign}${formatCurrency(fortnightlyDiff)}/fn`
        target.className =
          `text-xs tabular-nums ${fortnightlyDiff >= 0 ? "text-green-600" : "text-red-600"}`
      }
    } catch {
      // Silently handle
    }

    this._highlightActiveKsButton(rate)
  }

  _ksResultTargetForRate(rate) {
    if (!this.hasKsResultTarget) return null
    return this.ksResultTargets.find(el => parseFloat(el.dataset.rate) === rate) || null
  }

  _deriveKsRate(result) {
    const gross = Number(result?.gross_salary) || 0
    const employee = Number(result?.kiwisaver_employee) || 0
    if (gross <= 0 || employee <= 0) return 0
    return Math.round((employee / gross) * 1000) / 10
  }

  async benchmarkUpdated(event) {
    const detail = event.detail
    if (!detail?.next_level || !this.salaryValue) {
      this._hideCareerProgression()
      return
    }

    await this._showCareerProgression(detail.next_level)
  }

  benchmarkCleared() {
    this._hideCareerProgression()
  }

  async _showCareerProgression(nextLevel) {
    if (!this.hasCareerProgressionTarget || !this.hasCareerProgressionTextTarget) return

    try {
      const { calculate } = await import("../modules/salary_lab/calculator_api")
      const result = await calculate({
        gross_salary: nextLevel.median,
        kiwisaver_perc: this.kiwisaverPercValue,
        include_student_loan: this.hasStudentLoanValue
      })

      const diff = result.net_salary - this.netSalaryValue
      const sign = diff >= 0 ? "+" : ""
      this.careerProgressionTextTarget.textContent =
        `As a ${nextLevel.variant_title} (median ${formatCurrency(nextLevel.median)}), ` +
        `your take-home would be ${sign}${formatCurrency(diff)}/year.`

      this.careerProgressionTarget.classList.remove("hidden")
    } catch {
      // Silently handle
    }
  }

  _hideCareerProgression() {
    if (this.hasCareerProgressionTarget) {
      this.careerProgressionTarget.classList.add("hidden")
    }
  }

  _renderScenarios(result) {
    if (this.hasCurrentSalaryLabelTarget) {
      this.currentSalaryLabelTarget.textContent = formatCurrency(result.gross_salary)
    }

    this._clearRiseResults()
    this._clearKsResults()

    if (this.hasKiwisaverImpactTarget) {
      if (result.kiwisaver_employee > 0) {
        this.kiwisaverImpactTarget.textContent =
          `Your KiwiSaver costs ${formatCurrency(result.kiwisaver_employee)}/year ` +
          `but your employer adds ${formatCurrency(result.kiwisaver_employer || 0)}.`
        this.kiwisaverImpactTarget.closest("[data-scenario-row]")?.classList.remove("hidden")
        this._highlightActiveKsButton(this.kiwisaverPercValue)
      } else {
        this.kiwisaverImpactTarget.closest("[data-scenario-row]")?.classList.add("hidden")
      }
    }

    if (this.hasStudentLoanImpactTarget) {
      if (result.student_loan > 0) {
        this.studentLoanImpactTarget.textContent =
          `Student loan costs ${formatCurrency(result.student_loan)}/year. ` +
          `Without it, you'd take home ${formatCurrency(result.net_salary + result.student_loan)}.`
        this.studentLoanImpactTarget.closest("[data-scenario-row]")?.classList.remove("hidden")
      } else {
        this.studentLoanImpactTarget.closest("[data-scenario-row]")?.classList.add("hidden")
      }
    }

    if (this.hasContractorLinkTarget) {
      const hourly = Math.round(result.gross_salary / (46 * 40))
      this.contractorLinkTarget.href = `/contractor-calculator/?rate=${hourly}&rate_type=hourly`
      this.contractorLinkTarget.textContent = `As a contractor at $${hourly}/hr →`
    }
  }

  _showRiseResult(amount, result) {
    const diff = result.net_salary - this.netSalaryValue
    const targetKey = `riseResult${Math.abs(amount / 1000)}k`
    const target = this[`has${targetKey.charAt(0).toUpperCase() + targetKey.slice(1)}Target`]
      ? this[`${targetKey}Target`] : null

    if (target) {
      target.textContent = `+${formatCurrency(diff)}/year take-home`
      target.classList.remove("hidden")
    }
  }

  _clearRiseResults() {
    ;["riseResult5k", "riseResult10k", "riseResult20k"].forEach(key => {
      const capitalized = key.charAt(0).toUpperCase() + key.slice(1)
      if (this[`has${capitalized}Target`]) {
        this[`${capitalized.charAt(0).toLowerCase() + capitalized.slice(1)}Target`].classList.add("hidden")
      }
    })
  }

  _clearKsResults() {
    if (!this.hasKsResultTarget) return
    this.ksResultTargets.forEach(el => {
      el.className = "hidden text-xs tabular-nums"
    })
  }

  _highlightActiveKsButton(activeRate) {
    if (!this.hasKsRateBtnTarget) return
    this.ksRateBtnTargets.forEach(btn => {
      const rate = parseFloat(btn.dataset.rate)
      if (rate === activeRate) {
        btn.classList.add("ring-2", "ring-cyan-600")
      } else {
        btn.classList.remove("ring-2", "ring-cyan-600")
      }
    })
  }
}
